import { useHistory, useParams } from '@jupiter/plugin-runtime/router';
import { useBoolean, useNumber, useRequest, useUrlParams } from '@byted/hooks';
import { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Col,
  Row,
  Space,
  Tag,
  Divider,
  Toast,
  Modal,
  Form,
  Tabs,
  Empty,
  Pagination,
  Spin,
} from '@douyinfe/semi-ui';
import {
  CommentOneSolid,
  PreviewOpen,
  CommentOne,
  GoodOne,
  Good,
  View,
} from '@okee-fe/react-icons-dy-open';
import { FormApi } from '@douyinfe/semi-ui/lib/es/form';
import dayjs from 'dayjs';
import { IllustrationNoResult } from '@douyinfe/semi-illustrations';
import Paragraph from '@douyinfe/semi-ui/lib/es/typography/paragraph';
import Text from '@douyinfe/semi-ui/lib/es/typography/text';
import { BaseRequestFnType } from '@byted/hooks/lib/useRequest/types';
import { forumUserService } from '@/api/forumUser';
import { toastError } from '@/utils/toastError';
import {
  BlockState,
  GetUserHomeRequest,
  BlackListState,
  ToggleFollowRequest,
  UpdateBlockStateByUidRequest,
  Post,
  GetUserHomeResponseData,
} from '@/typings/open.platform.forum_bff/namespaces/forumuser';
import { useUserInfo } from '@/hooks/useUserInfo';
import useBiz from '@/context/useBiz';
import { Int64 } from '@/typings/open.platform.forum_bff';
import { IconPost } from '@/components/IconPost';

function useBlock() {
  return useRequest(async (params: UpdateBlockStateByUidRequest) => {
    try {
      const res = await forumUserService.updateBlockStateByUid(params);
      if (res.error) {
        throw new Error(res.message);
      }
      Toast.success(
        params.BlockState === BlockState.BLOCK ? '封禁成功' : '解除封禁成功',
      );
    } catch (e) {
      toastError(e);
    }
  });
}

function useUserInfoById() {
  const biz = useBiz(state => state.biz);
  const { id } = useParams<{
    id: string;
  }>();
  const { run, data, loading } = useRequest(
    async (params: GetUserHomeRequest) => {
      try {
        const res = await forumUserService.getUserHome(params);
        if (res.error) {
          throw new Error(res.message);
        }
        return res.data;
      } catch (e) {
        toastError(e);
      }
      return undefined;
    },
    {
      auto: true,
      ready: Boolean(id) && biz !== 0,
      refreshDeps: [id, biz],
      defaultParams: [
        {
          Uid: '',
          ToUserId: id,
          Biz: biz,
        },
      ],
    },
  );

  return { userInfo: data, gettingUserInfo: loading, refetch: run };
}

function useToggleFollow() {
  return useRequest(async (params: ToggleFollowRequest) => {
    try {
      const res = await forumUserService.toggleFollow(params);
      if (res.error) {
        throw new Error(res.message);
      }
      Toast.success('操作成功');
      return res.data;
    } catch (e) {
      toastError(e);
    }
    return undefined;
  });
}

const BanButton: React.FC<{
  refetch: BaseRequestFnType<
    GetUserHomeResponseData | undefined,
    [params: GetUserHomeRequest]
  >;
  otherUserInfo?: GetUserHomeResponseData;
}> = ({ refetch, otherUserInfo }) => {
  const { User: currentUser, isAdmin } = useUserInfo();
  const [visible, setVisible] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const { run: block } = useBlock();
  const [api, setApi] = useState<FormApi<{ time: Int64; reason: string }>>();
  const isBanned =
    otherUserInfo?.Black?.State &&
    otherUserInfo.Black.State !== BlackListState.Unblock;
  const biz = useBiz(s => s.biz);

  if (!isAdmin || currentUser?.Id === otherUserInfo?.User?.Id) {
    return null;
  }

  if (isBanned) {
    return (
      <>
        <Button
          className="w-[80px]"
          type={'tertiary'}
          onClick={() => {
            Modal.warning({
              title: '确认解除封禁？',
              async onOk() {
                try {
                  await block({
                    UserId: otherUserInfo?.User?.Id,
                    BlockState: BlockState.UN_BLOCK,
                    AdminBiz: biz,
                    AdminUid: '',
                  });
                  await refetch({
                    Uid: '',
                    ToUserId: otherUserInfo?.User?.Id,
                    Biz: biz,
                  });
                } catch (e) {
                  toastError(e);
                }
              },
            });
          }}>
          取消封禁
        </Button>
      </>
    );
  }

  return (
    <>
      <Button
        type={'danger'}
        className="w-[80px]"
        onClick={() => {
          setVisible(true);
        }}>
        封禁
      </Button>
      <Modal
        onOk={async () => {
          await api?.validate();
          try {
            await block({
              UserId: otherUserInfo?.User?.Id,
              AdminBiz: biz,
              AdminUid: '',
              BlockState: BlockState.BLOCK,
              ExpiredAt: `${api?.getValue('time') ?? ''}`,
              Reason: api?.getValue('reason'),
            });
            await refetch({
              Uid: '',
              ToUserId: otherUserInfo?.User?.Id,
              Biz: biz,
            });
          } catch (e) {
            toastError(e);
          } finally {
            setVisible(false);
          }
        }}
        visible={visible}
        title="确认封禁"
        closable={true}
        okText="封禁"
        okButtonProps={{ type: 'danger' }}
        onCancel={() => setVisible(false)}>
        <Form getFormApi={setApi}>
          <Form.Select
            field="time"
            className="w-full"
            noLabel={true}
            placeholder="请选择封禁时长"
            rules={[{ required: true, message: '请选择封禁时长' }]}
            optionList={[
              {
                label: '7 天',
                value: 7,
              },
              {
                label: '14 天',
                value: 14,
              },
              {
                label: '30 天',
                value: 30,
              },
              {
                label: '永久',
                value: -1,
              },
            ]}
          />
          <Form.Input
            field="reason"
            className="w-full"
            noLabel={true}
            placeholder="请输入封禁原因"
            onChange={value => {
              setWordCount(value.length);
            }}
            rules={[{ required: true, message: '请输入封禁原因' }]}
            maxLength={100}
            suffix={
              <span className="text-semi-color-text-2">{wordCount}/100</span>
            }
          />
        </Form>
      </Modal>
    </>
  );
};

const UserPanel: React.FC = () => {
  const { userInfo: otherUserInfo, refetch } = useUserInfoById();
  const biz = useBiz(s => s.biz);
  const { run: toggleFollow, loading } = useToggleFollow();
  const { ifLogin, User: currentUser } = useUserInfo();
  const isOwner = currentUser?.Id && currentUser.Id === otherUserInfo?.User?.Id;

  const toLogin = () => {
    window.Garfish.channel.emit('main:login-activate');
  };

  const isBanned =
    otherUserInfo?.Black &&
    otherUserInfo.Black.State !== BlackListState.Unblock;

  const blockedDaysText =
    isBanned &&
    (otherUserInfo?.Black?.State === BlackListState.Forever
      ? '账号违规，永久封禁'
      : `账号违规，封禁${otherUserInfo?.Black?.RestDays ?? 0}天`);

  const { state: followedNumber, inc, dec, set } = useNumber();

  const { state: ifFollowed, setTrue, setFalse, toggle } = useBoolean(false);

  useEffect(() => {
    if (otherUserInfo?.IsFollowed) {
      setTrue();
    } else {
      setFalse();
    }
    set(Number(otherUserInfo?.UserSummary?.Followed ?? 0));
  }, [otherUserInfo]);

  const handleClick = async () => {
    if (!ifLogin) {
      toLogin();
      return;
    }

    await toggleFollow({
      Biz: biz,
      ToUserId: otherUserInfo?.User?.Id,
    });

    if (ifFollowed) {
      dec();
    } else {
      inc();
    }
    toggle();
  };

  return (
    <div className="bg-white py-6 px-8 rounded-xl mb-4">
      <div className="flex justify-between items-center w-full">
        <div className="flex items-center">
          <Avatar
            src={otherUserInfo?.User?.Avatar}
            className="w-[72px] h-[72px] mr-4"
          />
          <div>
            <div className="font-medium text-xl mb-2">
              <Space align="center">
                {otherUserInfo?.User?.Name ?? '用户名'}
                {otherUserInfo?.User?.IfAdmin && (
                  <Tag className="text-semi-color-primary bg-[#3C89FF26]">
                    管理员
                  </Tag>
                )}
              </Space>
            </div>
            <div className="text-semi-color-text-2 text-base leading-5">
              <span className="mr-2">
                关注了{' '}
                {Math.max(0, Number(otherUserInfo?.UserSummary?.Follow ?? 0))}
              </span>
              <span className="">关注者 {Math.max(followedNumber, 0)}</span>
            </div>
          </div>
        </div>
        <div className=" space-x-2">
          {isBanned && <Button type="danger">{blockedDaysText}</Button>}
          {!isOwner && !isBanned && (
            <Button
              loading={loading}
              className="w-[80px]"
              type={ifFollowed ? 'tertiary' : 'primary'}
              theme={ifFollowed ? 'light' : 'solid'}
              onClick={handleClick}>
              {ifFollowed ? '取消关注' : '关注'}
            </Button>
          )}

          <BanButton otherUserInfo={otherUserInfo} refetch={refetch} />
        </div>
      </div>
      <IconPost />
    </div>
  );
};

const Achievement: React.FC = () => {
  const { userInfo: otherUserInfo } = useUserInfoById();
  return (
    <div className="bg-white rounded-xl px-8 py-6 w-[396px] ml-auto">
      <div className="font-medium text-xl mb-6 text-semi-color-text-0">
        个人成就
      </div>
      <div className="text-semi-color-text-1">
        <div className="mb-4 flex">
          <Space align="center">
            <div className="rounded-full w-6 h-6 bg-semi-color-primary-light-default flex items-center justify-center">
              <GoodOne className="text-semi-color-primary" />
            </div>
            发布内容获得点赞{' '}
            <span className="font-bold text-semi-color-text-0">
              {otherUserInfo?.UserSummary?.Liked ?? 0}
            </span>{' '}
            次
          </Space>{' '}
        </div>
        <div className="mb-4">
          <Space align="center">
            <div className="rounded-full w-6 h-6 bg-semi-color-primary-light-default flex items-center justify-center">
              <CommentOneSolid className="text-semi-color-primary" />
            </div>
            发布内容获得讨论{' '}
            <span className="font-bold text-semi-color-text-0">
              {Number(otherUserInfo?.UserSummary?.Commented ?? 0) +
                Number(otherUserInfo?.UserSummary?.Reply ?? 0)}
            </span>{' '}
            次
          </Space>{' '}
        </div>
        <div className="mb-4">
          <Space align="center">
            <div className="rounded-full w-6 h-6 bg-semi-color-primary-light-default flex items-center justify-center">
              <PreviewOpen className="text-semi-color-primary" />
            </div>
            文章被阅读{' '}
            <span className="font-bold text-semi-color-text-0">
              {otherUserInfo?.UserSummary?.ShareViewed ?? 0}
            </span>{' '}
            次
          </Space>{' '}
        </div>
      </div>
      <Divider className="w-full mb-4 " />
      <div className="text-semi-color-text-2 text-base leading-5 h-5">
        于 {dayjs(otherUserInfo?.User?.CreatedAt).format('YYYY-MM-DD')}{' '}
        加入抖音小程序开发者社区
      </div>
    </div>
  );
};

const tabNames = [
  {
    name: 'question',
    label: '问答帖',
  },
  {
    name: 'share',
    label: '文章',
  },
  {
    name: 'comment',
    label: '评论',
  },
  {
    name: 'reply',
    label: '回复',
  },
];

interface Query {
  page?: number;
  limit?: number;
  activeKey?: string;
}

interface PostsParams {
  activeKey: string;
  limit: number;
  page: number;
}

function useGetPosts(options: PostsParams) {
  const biz = useBiz(s => s.biz);
  const { id: toUserId } = useParams<{ id: string }>();
  const { activeKey, limit, page } = options;
  const { data, loading } = useRequest(
    async () => {
      try {
        if (['bulletin', 'question', 'share'].includes(activeKey)) {
          const res = await forumUserService.getPosts({
            Uid: '', // 当前登录用户的 uid
            ToUserId: toUserId, // 谁的用户主页 (Uid 查看了 ToUserId 的用户主页)
            Type: activeKey, // 帖子类型：all/question/share/bulletin
            PageNum: page, // 页码，选填，从 1 开始，默认为 1
            PageSize: limit, // 每页条数，选填，默认为 10
            Biz: biz, // 业务线
          });
          if (res.error) {
            throw new Error(res.message);
          }
          return {
            total: res.data.Total,
            list: res.data.Posts,
          };
        }

        if (activeKey === 'reply') {
          const res = await forumUserService.getReplies({
            ToUserId: toUserId,
            PageNum: page,
            PageSize: limit,
            Biz: biz,
            Uid: '',
          });
          if (res.error) {
            throw new Error(res.message);
          }
          return {
            total: res.data.Total,
            list: res.data.Replies,
          };
        }

        if (activeKey === 'comment') {
          const res = await forumUserService.getComment({
            Uid: '',
            ToUserId: toUserId,
            PageNum: page,
            PageSize: limit,
            Biz: biz,
          });
          if (res.error) {
            throw new Error(res.message);
          }
          return {
            total: res.data.Total,
            list: res.data.Comments,
          };
        }
      } catch (e) {
        toastError(e);
      }
      return {
        total: 0,
        list: [],
      };
    },
    {
      ready: biz !== 0,
      auto: true,
      refreshDeps: [toUserId, page, limit, biz, activeKey],
    },
  );
  return { data, loading };
}

const List: React.FC<{
  query: Query;
  setQuery: React.Dispatch<React.SetStateAction<Query>>;
}> = ({ query, setQuery }) => {
  const history = useHistory();
  const { page = 1, limit = 10, activeKey = 'question' } = query;
  const { id: toUserId } = useParams<{ id: string }>();
  const { data, loading } = useGetPosts({
    activeKey,
    limit,
    page,
  });
  const { User, isAdmin } = useUserInfo();
  const isOwner = User?.Id && User.Id === toUserId;

  // 创建帖子的 scope
  const scope = 'mini-app';
  if (loading) {
    return (
      <div className="m-0 flex justify-center items-center h-full absolute left-0 top-0 right-0 bottom-0">
        <Spin />
      </div>
    );
  }

  if (!data?.list?.length) {
    return (
      <Empty
        image={<IllustrationNoResult className="mt-[108px]" />}
        className="h-[516px] mx-auto"
        description={
          ['question', 'share', 'bulletin'].includes(activeKey)
            ? '暂未发布过帖子'
            : '暂无内容'
        }>
        {['question', 'share', 'bulletin'].includes(activeKey) && isOwner && (
          <div className="flex justify-center">
            <Button
              type="primary"
              theme="solid"
              className="px-6"
              onClick={() => {
                history.push(`/${scope}/post/new`);
              }}>
              {activeKey === 'question' ? '去提问' : '去发布'}
            </Button>
          </div>
        )}
      </Empty>
    );
  }

  return (
    <>
      {data.list.map(item => (
        <div
          key={item.Id}
          className="py-6 border-b cursor-pointer"
          onClick={() => {
            let id;
            if ('PostId' in item) {
              id = item.PostId;
            } else {
              id = item.Id;
            }

            history.push(`/${scope}/post/${id ?? ''}`);
          }}>
          <div className="font-medium text-base mb-3">
            <h6 className="font-medium flex items-center">
              {!item.Visible && isAdmin && (
                <Tag color="red" className="mr-2">
                  自见
                </Tag>
              )}
              <Text
                type="tertiary"
                className="font-normal text-lg leading-[22px]">
                {activeKey === 'comment' && '评论了 '}
                {activeKey === 'reply' && '回复了'}
              </Text>
              <Text type="primary" className="text-lg leading-[22px] ml-1">
                {item.Title || '空标题'}
              </Text>
            </h6>
          </div>
          <Paragraph type="secondary" className="mb-4" ellipsis={{ rows: 2 }}>
            {item.Text}
          </Paragraph>
          <div className="flex">
            <Text type="tertiary">
              {dayjs(item.CreatedAt).format('YYYY-MM-DD HH:mm')}
            </Text>
            <div className="ml-auto flex space-x-6">
              <Space align="center">
                <Good />
                <Text type="tertiary">{item.Liked ?? 0}</Text>
              </Space>
              {['bulletin', 'question', 'share'].includes(activeKey) && (
                <>
                  <Space align="center">
                    <CommentOne />
                    <Text type="tertiary">
                      {(item as Post).CommentCount ?? 0}
                    </Text>
                  </Space>
                  <Space align="center">
                    <View />
                    <Text type="tertiary">{(item as Post).ViewCount ?? 0}</Text>
                  </Space>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
      <Pagination
        total={data.total ?? 0}
        pageSize={limit}
        currentPage={page}
        hideOnSinglePage={true}
        className="mt-4 flex justify-end"
        onChange={(cur, size) =>
          setQuery({
            ...query,
            page: cur,
            limit: size,
          })
        }
      />
    </>
  );
};

const Summary: React.FC = () => {
  const { userInfo: otherUserInfo } = useUserInfoById();
  const { value: query, setValue: setQuery } = useUrlParams<Query>({
    activeKey: 'question',
  });
  return (
    <div className="bg-white rounded-xl py-6 px-8 min-h-[600px] relative mb-8">
      <Tabs
        type="line"
        activeKey={query.activeKey}
        onChange={key => setQuery({ ...query, page: 1, activeKey: key })}>
        {(otherUserInfo?.User?.WasAdmin || otherUserInfo?.User?.IfAdmin) && (
          <Tabs.TabPane key={'bulletin'} itemKey={'bulletin'} tab={'公告帖'} />
        )}
        {tabNames.map(tab => (
          <Tabs.TabPane key={tab.name} itemKey={tab.name} tab={tab.label} />
        ))}
      </Tabs>
      <List query={query} setQuery={setQuery} />
    </div>
  );
};

export const User = () => (
  <div className=" w-[1220px]  mx-auto mt-8">
    <UserPanel />
    <Row>
      <Col span={16}>{<Summary />}</Col>
      <Col span={8}>
        <Achievement />
      </Col>
    </Row>
  </div>
);

export default User;
